import React from 'react';
import styled from 'styled-components';

import { StarRating } from 'components/reviews/star-rating';
import { EffectChip } from 'components/form-inputs/effects-chips-input/effect-chip';
import type { ProductReview } from './use-product-reviews';
import { maskReviewerName, reviewerInitial, formatReviewDate } from './review-utils';

type ReviewCardProps = {
  review: ProductReview;
};

export const ReviewCard = ({ review }: ReviewCardProps): JSX.Element => {
  const name = maskReviewerName(review.reviewerFirstName, review.reviewerLastName);
  const initial = reviewerInitial(review.reviewerFirstName, review.reviewerLastName);

  return (
    <Card data-testid='review-card'>
      <Header>
        <Identity>
          <Avatar aria-hidden='true'>{initial}</Avatar>
          <Name>{name}</Name>
          <StarRating value={review.rating} size={14} />
        </Identity>
        <DateText>{formatReviewDate(review.createdAt)}</DateText>
      </Header>

      {review.body && <Body>{review.body}</Body>}

      {review.effects.length > 0 && (
        <Effects>
          {review.effects.map((effect) => (
            <EffectChip key={effect} effect={effect} variant='plain' />
          ))}
        </Effects>
      )}
    </Card>
  );
};

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.grey[90]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.grey[90]};
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 14px;
  font-weight: 600;
`;

const Name = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const DateText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[45]};
  white-space: nowrap;
`;

const Body = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const Effects = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;
